import { LOCALES } from '../../constants/intl';
import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import IIntlService from '../intl/IIntlService';
import IntlService from '../intl/IntlService';
import UniqueId from '../uid/UniqueId';

import IMenuService from './IMenuService';
import { MenuItemViewModelType } from './MenuItemViewModel';
import MenuViewModel from './MenuViewModel';
import MenuViewModelBuilder from './MenuViewModelBuilder';

/**
 * Application menu service.
 */
class MenuServiceSingleton implements IMenuService {
  private readonly apiProvider: IApiProvider;
  private readonly schemaRepository: ISchemaRepository;
  private readonly intlService: IIntlService;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
    this.schemaRepository = new CachedSchemaRepository();
    this.intlService = new IntlService();
  }

  public async getMenu(application: string): Promise<MenuViewModel> {
    const menuSchema = await this.schemaRepository.getMenu(application);
    const allowedCategories =
      await this.apiProvider.auth.getAllowedCategories();

    const viewModelBuilder = new MenuViewModelBuilder(
      application,
      allowedCategories
    );

    for (const item of menuSchema.items) {
      await viewModelBuilder.addRootItem(item);
    }

    return viewModelBuilder.build();
  }

  public filterMenu(
    filter: string,
    menuItems: MenuViewModel['items'],
    locale: LOCALES
  ): UniqueId[] {
    const multiLangFilter: string[] = this.intlService.getUserInputConversions(
      filter,
      locale
    );
    return filter.length === 0
      ? []
      : Object.values(menuItems)
          .filter(
            item =>
              item.type !== MenuItemViewModelType.SEPARATOR &&
              multiLangFilter.some(val =>
                item.label.toLowerCase().includes(val.toLowerCase())
              )
          )
          .map(item => item.id);
  }
}

const MenuService = (() => {
  let instance: IMenuService;
  return new Proxy(MenuServiceSingleton, {
    construct(Target: typeof MenuServiceSingleton): IMenuService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default MenuService;
